import { useEffect, useRef } from 'react';
import { gameEvents } from '../game/events';
import { isNewHandForMe } from '../game/turnStatus';
import { useGame } from '../stores/game';
import { useSession } from '../stores/session';
import { music } from './music';
import type { Track } from './music';
import { sfx } from './sfx';

/** Música de fondo según la pantalla. */
export function useMusic(track: Track | null): void {
  useEffect(() => {
    music.setTrack(track);
  }, [track]);
}

/** Sonidos de la partida a partir de las entradas nuevas del log y del chat. */
export function useGameAudio(): void {
  const nudged = useRef<number>(-1);
  useEffect(() => {
    const offView = gameEvents.subscribe((entries, prev, next) => {
      const mySeat = next.me?.seat;
      for (const e of entries) {
        switch (e.type) {
          case 'turnResolved': {
            const kinds = new Set(e.plays.map((p) => p.action));
            if (kinds.has('wonder')) sfx.wonder();
            else if (kinds.has('build')) sfx.build();
            if (kinds.has('discard') || kinds.has('frozenDiscard')) window.setTimeout(sfx.discard, 120);
            const mine = e.plays.find((p) => p.seat === mySeat);
            const moved = mine ? mine.gained + mine.paid.left + mine.paid.right + mine.paid.bank : 0;
            if (moved > 0) window.setTimeout(() => sfx.coins(Math.min(8, moved)), 260);
            break;
          }
          case 'buildFromDiscard':
          case 'playLastCard':
            sfx.build();
            break;
          case 'military': {
            const r = e.results.find((x) => x.seat === mySeat);
            sfx.battle(r ? r.left + r.right >= 0 : true);
            break;
          }
          case 'ageStart':
            if (e.age > 1) window.setTimeout(sfx.horn, 1400);
            break;
          case 'gameOver':
            window.setTimeout(sfx.fanfare, 900);
            break;
        }
      }
      // ¡Te toca!: llega tu nueva mano. Tras un cambio de era, después de la batalla y el cuerno.
      if (isNewHandForMe(prev, next)) {
        const newAge = entries.some((e) => e.type === 'ageStart' && e.age > 1);
        window.setTimeout(sfx.yourTurn, newAge ? 2600 : 450);
      }
      // Recordatorio suave cuando solo faltas tú.
      if (next.decision.kind === 'turn' && next.me && !next.me.selection) {
        const pending = next.players.filter((p) => p.status === 'active' && !p.ready);
        if (pending.length === 1 && pending[0]!.seat === mySeat && nudged.current !== next.decisionId) {
          nudged.current = next.decisionId;
          sfx.bell();
        }
      }
    });
    const offChat = gameEvents.subscribeChat((m) => {
      const me = useSession.getState().user?.id;
      if (m.kind === 'emote') sfx.pop();
      else if (m.kind === 'text' && m.userId !== me) sfx.bell();
    });
    return () => {
      offView();
      offChat();
    };
  }, []);

  const age = useGame((g) => g.view?.age);
  const over = useGame((g) => g.view?.decision.kind === 'gameOver');
  useMusic(over ? 'lobby' : age ? (`age${age}` as Track) : null);
}
